import { useState, useCallback } from 'react';
import { palette } from '../../styles/tokens';
import { supabase } from '../../lib/supabase';
import { PhoneInput } from './PhoneInput';
import type { Client } from '../../types';

/**
 * InlineCreateClientForm — used inside ClientPicker's create-on-fly dropdown
 * row. Single Name field + Phone/Email tab toggle. Submits via the
 * create_client RPC (mig 189) which dedupes by phone-then-email within the
 * tenant. On duplicate, surfaces an in-form prompt to "Use existing" or
 * "Keep separate" — the parent decides which clientId to bubble up.
 *
 * Q5 Phase 2 (Session 76).
 */

export interface InlineCreateClientResult {
  clientId: string;
  name: string;
  phone: string | null;
  email: string | null;
}

export interface InlineCreateClientFormProps {
  initialName?: string;
  onCreated: (result: InlineCreateClientResult) => void;
  onCancel: () => void;
}

type Tab = 'phone' | 'email';
type DupePrompt = { existing: Client } | null;

const WRAPPER: React.CSSProperties = {
  background: palette.g50,
  border: `1px solid ${palette.g200}`,
  borderRadius: 8,
  padding: 12,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
};

const LABEL: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: palette.g600,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const INPUT: React.CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  borderRadius: 8,
  border: `1px solid ${palette.g200}`,
  background: palette.white,
  fontFamily: 'inherit',
  fontSize: 13.5,
  color: palette.g800,
  outline: 'none',
  boxSizing: 'border-box',
};

const TAB_ROW: React.CSSProperties = {
  display: 'flex',
  gap: 0,
  borderRadius: 8,
  border: `1px solid ${palette.g200}`,
  overflow: 'hidden',
  background: palette.white,
};

const TAB_BTN_BASE: React.CSSProperties = {
  flex: 1,
  padding: '7px 12px',
  border: 'none',
  background: 'transparent',
  fontSize: 13,
  fontWeight: 500,
  color: palette.g600,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const TAB_BTN_ACTIVE: React.CSSProperties = {
  ...TAB_BTN_BASE,
  background: palette.accentWash,
  color: palette.accentDeep,
  fontWeight: 600,
};

const ROW: React.CSSProperties = {
  display: 'flex',
  gap: 8,
  alignItems: 'center',
};

const PRIMARY: React.CSSProperties = {
  flex: 1,
  padding: '8px 14px',
  borderRadius: 8,
  border: 'none',
  background: palette.accent,
  color: palette.white,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const PRIMARY_DISABLED: React.CSSProperties = {
  ...PRIMARY,
  background: palette.g200,
  color: palette.g500,
  cursor: 'not-allowed',
};

const SECONDARY: React.CSSProperties = {
  padding: '8px 12px',
  borderRadius: 8,
  border: `1px solid ${palette.g200}`,
  background: palette.white,
  color: palette.g700,
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const ERROR_TEXT: React.CSSProperties = {
  fontSize: 12,
  color: palette.red,
  marginTop: -4,
};

const DUPE_BOX: React.CSSProperties = {
  background: palette.white,
  border: `1px solid ${palette.accent}`,
  borderRadius: 6,
  padding: 10,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  fontSize: 13,
  color: palette.g800,
};

export function InlineCreateClientForm({
  initialName = '',
  onCreated,
  onCancel,
}: InlineCreateClientFormProps) {
  const [name, setName] = useState(initialName);
  const [tab, setTab] = useState<Tab>('phone');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dupe, setDupe] = useState<DupePrompt>(null);

  const trimmedName = name.trim();
  const trimmedEmail = email.trim();
  const phoneValid = tab === 'phone' && phone.replace(/\D/g, '').length === 10;
  const emailValid = tab === 'email' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);
  const canSubmit =
    trimmedName.length > 0 && (phoneValid || emailValid) && !submitting;

  const submit = useCallback(async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);

    const { data, error: rpcErr } = await supabase.rpc('create_client', {
      p_name: trimmedName,
      p_phone: tab === 'phone' ? phone : null,
      p_email: tab === 'email' ? trimmedEmail : null,
    });

    setSubmitting(false);

    if (rpcErr) {
      setError(rpcErr.message);
      return;
    }
    const result = (data ?? {}) as {
      duplicate?: boolean; existing?: Client; client?: Client; error?: string;
    };
    if (result.error) {
      const msg = result.error === 'no_business'      ? 'No business context'
                : result.error === 'name_required'    ? 'Name is required'
                : result.error === 'contact_required' ? 'Phone or email required'
                : result.error;
      setError(msg);
      return;
    }
    if (result.duplicate && result.existing) {
      setDupe({ existing: result.existing });
      return;
    }
    if (result.client) {
      onCreated({
        clientId: result.client.id,
        name: result.client.name,
        phone: result.client.phone,
        email: result.client.email,
      });
    }
  }, [canSubmit, trimmedName, tab, phone, trimmedEmail, onCreated]);

  if (dupe) {
    const e = dupe.existing;
    const matchKind = tab === 'phone' ? `${e.name}'s phone` : `${e.name}'s email`;
    return (
      <div style={WRAPPER}>
        <div style={DUPE_BOX}>
          <div>
            <strong>{trimmedName}</strong> matches {matchKind}. Use {e.name} instead?
          </div>
          <div style={ROW}>
            <button
              type="button"
              style={PRIMARY}
              onClick={() => onCreated({
                clientId: e.id, name: e.name, phone: e.phone, email: e.email,
              })}
            >
              Use {e.name.split(' ')[0]}
            </button>
            <button type="button" style={SECONDARY} onClick={onCancel}>Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={WRAPPER}>
      <div>
        <div style={LABEL}>Name</div>
        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="First Last"
          style={{ ...INPUT, marginTop: 4 }}
        />
      </div>

      <div style={TAB_ROW}>
        <button
          type="button"
          style={tab === 'phone' ? TAB_BTN_ACTIVE : TAB_BTN_BASE}
          onClick={() => setTab('phone')}
        >
          Phone
        </button>
        <button
          type="button"
          style={tab === 'email' ? TAB_BTN_ACTIVE : TAB_BTN_BASE}
          onClick={() => setTab('email')}
        >
          Email
        </button>
      </div>

      {tab === 'phone' ? (
        <PhoneInput value={phone} onChange={setPhone} placeholder="(555) 123-4567" />
      ) : (
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="name@example.com"
          style={INPUT}
        />
      )}

      {error && <div style={ERROR_TEXT}>{error}</div>}

      <div style={ROW}>
        <button
          type="button"
          style={canSubmit ? PRIMARY : PRIMARY_DISABLED}
          disabled={!canSubmit}
          onClick={() => { void submit(); }}
        >
          {submitting ? 'Adding…' : 'Add Client'}
        </button>
        <button type="button" style={SECONDARY} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}
